"use client";

import { Fragment } from "react";
import Image from "next/image";
import ScrollReveal from "../common/ScrollReveal";
import BarHeading from "../common/BarHeading";

/** Dot bullet used across the page (Figma Frame 3577). */
function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-4">
      <span
        aria-hidden
        className="mt-[10px] block h-2 w-2 shrink-0 rounded-full bg-primary"
      />
      <p className="flex-1 min-w-0 text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
        {children}
      </p>
    </li>
  );
}

/** "What DarkSight monitors" leak categories (Figma 428:11427). */
const MONITORS: { label: string; text: string }[] = [
  {
    label: "Account Leak:",
    text: "Employee emails & passwords exposed in targeted breaches and traded on the dark web.",
  },
  {
    label: "Combo List:",
    text: "Username-password pairs from past leaks, fueling credential-stuffing attacks.",
  },
  {
    label: "Malware Infection:",
    text: "Credentials stolen by infostealer malware, with infected host and file path identified.",
  },
];

/** Target industries, two balanced columns (Figma 2256:57474). */
const INDUSTRIES: string[][] = [
  ["Financial Services", "Technology", "Healthcare"],
  ["Manufacturing", "Retail", "Critical Infrastructure"],
];

/** "How it works" pipeline steps (Figma 2256:65771). */
const STEPS: { title: string; desc: string }[] = [
  { title: "MONITOR", desc: "Continuous dark web scanning" },
  { title: "ANALYZE", desc: "Risk scoring & prioritization" },
  { title: "RESPOND", desc: "Action tracking & remediation" },
];

/** "Why enterprises buy" value props (Figma 2256:65753). */
const VALUE_PROPS: { title: string; desc: string }[] = [
  { title: "Early Detection", desc: "Spot leaks before attackers exploit them." },
  { title: "Prioritized Response", desc: "Risk-scored alerts focus team effort." },
  { title: "Daily Monitoring", desc: "Continuous coverage, not point-in-time." },
  { title: "Response Tracking", desc: "Built-in workflow & accountability." },
];

export default function DarksightProduct() {
  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pt-2 pb-16 overflow-hidden">
      <div className="max-w-[1300px] mx-auto flex flex-col gap-8 md:gap-8 lg:gap-10">
        {/* Pipeline of 3 steps — leads the page directly under the product tabs,
            so its own "How it works" heading is dropped (the figure reads on its
            own and a heading here would compete with the page title above). */}
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-0">
          {STEPS.map((step, i) => (
            <Fragment key={step.title}>
              <ScrollReveal
                direction="up"
                delay={i * 100}
                className="lg:flex-[311_1_0]"
              >
                <div className="flex h-full flex-col items-center justify-center gap-2 rounded-[8px] bg-gradient-to-b from-[#2e353a] from-[14%] to-background p-4 text-center min-h-[95px]">
                  <p className="font-[family-name:var(--font-avenir)] font-semibold text-primary text-[18px] sm:text-[20px] leading-[1.25] tracking-[0.15px]">
                    {step.title}
                  </p>
                  <p className="text-center text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                    {step.desc}
                  </p>
                </div>
              </ScrollReveal>

              {/* Connector spanning the gap between steps (Figma 2256:65833):
                  an arrowhead flush to the card the pulse leaves, a line out
                  of it, and a ring+dot sliding along that line into the next
                  card. Mobile is the same figure turned 90° — only the head,
                  the ring and the loop duration are scaled down to suit the
                  much shorter path. */}
              {i < STEPS.length - 1 && (
                <>
                  {/* Desktop — horizontal */}
                  <div
                    aria-hidden
                    className="relative z-10 hidden lg:flex lg:flex-[182_1_0] items-center"
                  >
                    {/* Line runs behind, from the arrowhead into the dot centre so it reads as joined */}
                    <span className="absolute left-[6px] right-0 h-[2px] bg-primary" />
                    {/* Travelling pulse: a red dot slides along the line from the
                        arrowhead to the destination ring, then loops. */}
                    <span aria-hidden className="ds-run-dot" />
                    {/* Arrowhead ▶ flush to the previous card */}
                    <span className="relative block h-0 w-0 border-y-[6px] border-l-[10px] border-y-transparent border-l-primary" />
                    {/* Static destination ring hidden — only the travelling ring shows. */}
                  </div>

                  {/* Mobile — vertical, mirroring the desktop order: line
                      behind, travelling pulse, then the ▼ head painted on top
                      at the start (flush to the card above). */}
                  <div aria-hidden className="lg:hidden relative z-10 h-16 w-full">
                    <span className="absolute top-[5px] bottom-0 left-1/2 -translate-x-1/2 w-[2px] bg-primary" />
                    <span aria-hidden className="ds-run-dot-v" />
                    <span className="absolute top-0 left-1/2 -translate-x-1/2 block h-0 w-0 border-x-[5px] border-t-[8px] border-x-transparent border-t-primary" />
                  </div>
                </>
              )}
            </Fragment>
          ))}
        </div>

        {/* Part 1: intro + leak categories + industries (left) | eye visual (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-[745fr_543fr] gap-8 lg:gap-12 items-start">
          <div className="flex flex-col gap-8">
            <ScrollReveal direction="up" delay={0}>
              <p className="font-[family-name:var(--font-avenir)] font-semibold text-text-highlight text-[15px] sm:text-[16px] leading-[24px] tracking-[0.5px] uppercase">
                Proactive cyber defense with real-time dark web threat
                intelligence
              </p>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={80}>
              <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                A subscription platform that continuously monitors the dark web
                for leaked credentials, stolen data, and malware-driven exposure
                — so your customers can detect, prioritize, and respond before
                attackers strike.
              </p>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={160}>
              <div className="flex flex-col gap-4">
                <BarHeading>What DarkSight Monitors</BarHeading>
                <ul className="flex flex-col gap-1">
                  {MONITORS.map((m) => (
                    <Bullet key={m.label}>
                      <span className="font-[family-name:var(--font-avenir)] font-semibold">
                        {m.label}
                      </span>{" "}
                      {m.text}
                    </Bullet>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={240}>
              <div className="flex flex-col gap-2">
                <BarHeading>Target Industries</BarHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 py-5">
                  {INDUSTRIES.map((col, ci) => (
                    <ul key={ci} className="flex flex-col gap-2">
                      {col.map((item) => (
                        <Bullet key={item}>{item}</Bullet>
                      ))}
                    </ul>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* On mobile the eye visual leads (order-first) so the page opens on
              the image instead of burying it under the intro/monitors/industries
              text; on lg it returns to the right-hand column (source order). */}
          <ScrollReveal
            direction="right"
            delay={200}
            className="order-first lg:order-none"
          >
            {/* The eye is near-square (543x510), so at full bleed on a phone it
                would stand ~340px tall and push the copy off-screen — cap the
                width below lg (same ladder idea as CompromiseAssessment). */}
            <div className="relative w-full max-w-[300px] sm:max-w-[420px] lg:max-w-[543px] mx-auto lg:ml-auto">
              <div className="relative aspect-[543/510] w-full">
                <Image
                  src="/images/Product/darksight/eye.svg"
                  alt="DarkSight — dark web threat intelligence visualization"
                  fill
                  sizes="(max-width: 1024px) 80vw, 543px"
                  className="object-contain"
                  priority
                />
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* Why enterprises buy — 4 value props as cards. Each one used to be
            bare copy behind a small red rule; the rule is gone and the card
            carries the grouping instead, reusing the same gradient panel as the
            pipeline steps at the top of this page. Note the red bar on the
            BarHeading above stays — that marks the section, not the items. */}
        <div className="flex flex-col gap-4">
          <ScrollReveal direction="up" delay={0}>
            <BarHeading>Why Enterprises Buy</BarHeading>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 w-full">
            {VALUE_PROPS.map((prop, i) => (
              <ScrollReveal
                key={prop.title}
                direction="up"
                delay={120 + i * 80}
                className="h-full"
              >
                <div className="flex h-full flex-col gap-2 rounded-[8px] bg-gradient-to-b from-[#2e353a] from-[14%] to-background p-5">
                  {/* Red picks up the accent the removed rule used to carry, and
                      matches the pipeline step titles in the same panel style at
                      the top of this page. */}
                  <p className="font-[family-name:var(--font-avenir)] font-semibold text-primary text-[18px] sm:text-[20px] leading-[1.25] tracking-[0.15px]">
                    {prop.title}
                  </p>
                  <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                    {prop.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>

      {/* "How it works" connector — travelling red pulse from arrowhead → ring */}
      <style jsx>{`
        /* Travelling marker is the ring+dot itself (matches the destination ⊙),
           so the whole circle slides along the line. */
        .ds-run-dot {
          position: absolute;
          top: 50%;
          left: 12px;
          width: 19px;
          height: 19px;
          margin: -9.5px 0 0 -9.5px;
          border-radius: 9999px;
          border: 1.5px solid #ec1938;
          background: transparent;
          box-shadow: 0 0 8px 1px rgba(236, 25, 56, 0.5);
          animation: dsRunDot 2s linear infinite;
        }
        .ds-run-dot::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 11px;
          height: 11px;
          transform: translate(-50%, -50%);
          border-radius: 9999px;
          background: #ec1938;
        }
        @keyframes dsRunDot {
          0% {
            left: 12px;
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          100% {
            left: 100%;
            opacity: 1;
          }
        }

        /* Vertical variant for the stacked mobile pipeline — same figure, only
           scaled down: 13px ring with a tighter halo (the 8px/1px desktop glow
           spreads the marker to ~37px of blur, which swamps a 64px connector),
           starting just past the ▼ head, and a 1s loop instead of 2s so the
           shorter path keeps a comparable pace rather than crawling at a third
           of the desktop speed. */
        .ds-run-dot-v {
          position: absolute;
          left: 50%;
          top: 10px;
          width: 13px;
          height: 13px;
          margin: -6.5px 0 0 -6.5px;
          border-radius: 9999px;
          border: 1.5px solid #ec1938;
          background: transparent;
          box-shadow: 0 0 5px 0 rgba(236, 25, 56, 0.45);
          animation: dsRunDotV 1s linear infinite;
        }
        .ds-run-dot-v::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 7px;
          height: 7px;
          transform: translate(-50%, -50%);
          border-radius: 9999px;
          background: #ec1938;
        }
        @keyframes dsRunDotV {
          0% {
            top: 10px;
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          100% {
            top: 100%;
            opacity: 1;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .ds-run-dot,
          .ds-run-dot-v {
            display: none;
          }
        }
      `}</style>
    </section>
  );
}
